// @givmo/board — a new member's own getting-started list (CBG-YR4-START, g5).
//
// The member's list, in their own words: what to read (each opens the record's own page, where the
// shipped act "Record that I’ve read this" names the exact version, signature-class in the signing
// window), and what to do (the year's conflict-of-interest questionnaire, CBG-YR2's own page; the
// board's own steps, which the member marks themselves like an RSVP; their board buddy, a fact).
// "About the board" leads, because it is where most of the reading starts. No count, no bar, no due
// date the board didn't set, and nothing about anyone else's list.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isGivmoError } from '@givmo/api-client';
import { Button, Card, CardBody, Skeleton, toast } from '@givmo/design-system';
import { GroupLabel, StatusWord } from '../../components/fine-print';
import { Link, useNavigate } from '../../board-routing';
import { BOARD_DIRECTOR_PAGES } from '../../board-routes';
import { useBoardDirectorOperations } from '../../board-runtime';
import {
  directorBoardIdentity,
  directorBoardSearch,
  useDirectorBoardAddress,
} from '../../director/director-board';
import { useDirectorReadsMayFire } from '../../director/read-gate';
import { todayAsDateInput } from '../../lib/display';
import { shortDay } from '../conflicts/coi-decode';
import { DirectorBoardContent } from './director-board-content';
import { DirectorBoardOnlyPage } from './director-board-only-page';
import { DirectorShell } from './director-shell';
import { isCommitteeOnly } from '../../director/director-wire';
import {
  buddyCapacity,
  confirmationWords,
  extractOwnList,
  itemDetail,
  itemTitle,
} from '../onboarding/onboarding-decode';
import { onboardingWords as w } from '../onboarding/onboarding-words';

const ROW =
  'flex flex-wrap items-center justify-between gap-x-16 gap-y-8 border-t border-border-subtle py-12 first:border-t-0 first:pt-0 last:pb-0';
const TITLE = 'font-sans text-s14 font-w600 tracking-givmo text-text-strong';
const SMALL = 'font-sans text-s12 font-w400 tracking-givmo text-text-tertiary';
const LINE = 'font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary';
const ACT =
  'font-sans text-s14 font-w600 tracking-givmo text-brand-600 outline-none hover:text-brand-700 focus-visible:underline';

export function directorGettingStartedKey(orgRef: string | null) {
  return ['board-director-getting-started', orgRef ?? ''] as const;
}

export function DirectorGettingStartedPage() {
  const { board } = useDirectorBoardAddress();
  // CBG-YR6-OUTSIDE (review-215's F5): A getting-started list is the board's alone; a seat for someone who isn't
  // on the board, here by address, meets the one not-found and nothing of the board's.
  if (isCommitteeOnly(board))
    return <DirectorBoardOnlyPage testId="page-director-getting-started" />;
  return <DirectorGettingStartedPageForTheBoard />;
}

function DirectorGettingStartedPageForTheBoard() {
  const operations = useBoardDirectorOperations();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { orgRef, board, notYours } = useDirectorBoardAddress();
  const mayFire = useDirectorReadsMayFire(orgRef);
  const listQuery = useQuery({
    queryKey: directorGettingStartedKey(orgRef),
    enabled: orgRef !== null && mayFire,
    retry: false,
    queryFn: async () => {
      const { data } = await operations.directorGetOwnBoardGettingStarted({
        path: { org_ref: orgRef ?? '' },
        throwOnError: true,
      });
      return extractOwnList(data);
    },
  });
  const markStep = useMutation({
    mutationFn: async ({ itemId, doneOn }: { itemId: string; doneOn: string | null }) => {
      await operations.directorMarkOwnBoardGettingStartedStep({
        path: { org_ref: orgRef ?? '', item_id: itemId },
        body: { done_on: doneOn },
        throwOnError: true,
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: directorGettingStartedKey(orgRef) }),
    onError: (caught) =>
      toast.error(isGivmoError(caught) ? caught.message : 'We couldn’t mark it. Please try again.'),
  });

  const list = listQuery.data ?? null;
  const search = orgRef === null ? undefined : directorBoardSearch(orgRef);
  const items = list?.items ?? [];
  const reads = items.filter((item) => item.item_kind === 'read' && item.available !== false);
  const disclosure = items.find((item) => item.item_kind === 'disclosure')?.disclosure ?? null;
  const steps = items.filter((item) => item.item_kind === 'step');
  const closed = Boolean(list?.closed);
  // CBG-FIX141 (review-291's M5a): a checklist the board has closed asks nothing more, so "To do"
  // keeps only what was done — each step marked done, the questionnaire once answered — and goes
  // when nothing was.
  const shownSteps = closed ? steps.filter((step) => Boolean(step.step_done_on)) : steps;
  const shownDisclosure =
    disclosure !== null &&
    disclosure.stage !== 'with_next_year' &&
    (!closed || disclosure.stage === 'answered')
      ? disclosure
      : null;
  const toDoShown = closed
    ? shownDisclosure !== null || shownSteps.length > 0
    : disclosure !== null || steps.length > 0;

  return (
    <DirectorShell
      title={w.directorTitle}
      description={board?.organization_name ?? undefined}
      identity={directorBoardIdentity(board)}
      testId="page-director-getting-started"
    >
      <DirectorBoardContent orgRef={orgRef} notYours={notYours}>
        {listQuery.isPending && mayFire ? <Skeleton className="h-84 w-full rounded-lg" /> : null}

        <Card testId="director-about-card">
          <CardBody className="flex flex-wrap items-center justify-between gap-12">
            <div className="flex min-w-0 flex-col gap-2">
              <span className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
                {w.aboutCardTitle}
              </span>
              <span className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
                {w.aboutCardLine}
              </span>
            </div>
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              onClick={() => void navigate({ to: BOARD_DIRECTOR_PAGES.about, search })}
              testId="director-about-open"
            >
              {w.open}
            </Button>
          </CardBody>
        </Card>

        {list !== null && !list.started ? (
          <p className={LINE} data-testid="director-gs-none">
            {w.noOwnList}
          </p>
        ) : null}
        {closed ? (
          <p className={LINE} data-testid="director-gs-closed">
            {w.ownListClosed}
          </p>
        ) : null}

        {reads.length > 0 ? (
          <Card testId="director-to-read">
            <CardBody className="flex flex-col gap-12">
              <div className="flex flex-col gap-4">
                <GroupLabel>{w.toReadGroup}</GroupLabel>
                {/* CBG-FIX141: once the board has closed the checklist it asks nothing more, so the
                    ask goes; the documents stay, since reading them is still allowed. */}
                {closed ? null : (
                  <p
                    className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
                    data-testid="director-gs-to-read-line"
                  >
                    {w.toReadLine}
                  </p>
                )}
              </div>
              <ul className="flex flex-col">
                {reads.map((item) => (
                  <li key={item.id} className={ROW} data-testid={`director-gs-${item.id}`}>
                    <div className="flex min-w-0 flex-col gap-2">
                      <span className={TITLE}>{itemTitle(item)}</span>
                      {itemDetail(item) !== null ? (
                        <span className={SMALL}>{itemDetail(item)}</span>
                      ) : null}
                    </div>
                    {item.confirmation ? (
                      <StatusWord tone="plain" testId={`director-gs-status-${item.id}`}>
                        {confirmationWords(item.confirmation)}
                      </StatusWord>
                    ) : (
                      <Link
                        to={BOARD_DIRECTOR_PAGES.record}
                        params={{ recordId: item.target_record_id ?? '' }}
                        search={{ ...(search ?? {}), from: 'getting-started' }}
                        className={ACT}
                      >
                        {w.read}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </CardBody>
          </Card>
        ) : null}

        {toDoShown ? (
          <Card testId="director-to-do">
            <CardBody className="flex flex-col gap-12">
              {closed ? null : <GroupLabel>{w.toDoGroup}</GroupLabel>}
              <ul className="flex flex-col">
                {shownDisclosure !== null ? (
                  <li className={ROW} data-testid="director-gs-disclosure">
                    <div className="flex min-w-0 flex-col gap-2">
                      <span className={TITLE}>{w.yourDisclosure(shownDisclosure.label ?? '')}</span>
                      <span className={SMALL}>
                        {shownDisclosure.stage === 'answered'
                          ? w.disclosureAnswered(
                              shownDisclosure.label ?? '',
                              shortDay(shownDisclosure.responded_at),
                            )
                          : shownDisclosure.stage !== 'sent'
                            ? w.notSentToYouYet
                            : shownDisclosure.joined_after_close || !shownDisclosure.due_on
                              ? w.disclosureSentNotAnswered(
                                  shownDisclosure.label ?? '',
                                  shortDay(shownDisclosure.sent_at),
                                )
                              : w.dueOn(shortDay(shownDisclosure.due_on))}
                      </span>
                    </div>
                    {shownDisclosure.stage === 'sent' && shownDisclosure.cycle_id ? (
                      <Button
                        appearance="saas"
                        tone="secondary"
                        size="sm"
                        onClick={() =>
                          void navigate({
                            to: BOARD_DIRECTOR_PAGES.annualDisclosure,
                            search: { ...(search ?? {}), cycle: shownDisclosure.cycle_id ?? '' },
                          })
                        }
                        testId="director-gs-disclosure-open"
                      >
                        {w.open}
                      </Button>
                    ) : null}
                  </li>
                ) : null}
                {shownSteps.map((step) => (
                  <li key={step.id} className={ROW} data-testid={`director-gs-step-${step.id}`}>
                    <div className="flex min-w-0 flex-col gap-2">
                      <span className={TITLE}>{itemTitle(step)}</span>
                      {step.step_done_on ? (
                        <span className={SMALL}>{w.doneOn(shortDay(step.step_done_on))}</span>
                      ) : null}
                    </div>
                    {!closed ? (
                      <button
                        type="button"
                        className={ACT}
                        disabled={markStep.isPending}
                        onClick={() =>
                          markStep.mutate({
                            itemId: step.id,
                            doneOn: step.step_done_on ? null : todayAsDateInput(),
                          })
                        }
                        data-testid={`director-gs-mark-${step.id}`}
                      >
                        {step.step_done_on ? w.undoDone : w.markOwnStepDone}
                      </button>
                    ) : null}
                  </li>
                ))}
              </ul>
            </CardBody>
          </Card>
        ) : null}

        {/* The buddy is a fact for the member, not a step: a quiet line under the list. */}
        {list?.buddy?.display_name ? (
          <p className={LINE} data-testid="director-gs-buddy">
            {w.yourBuddyLine(list.buddy.display_name, buddyCapacity(list.buddy) || null)}
          </p>
        ) : null}
      </DirectorBoardContent>
    </DirectorShell>
  );
}
